/**
 * Convert common color strings to a tuple containing each RGB value.
 *
 * E.g., #345 and rgb(33,44,55) are transformed into [33, 44, 55].
 */
export function colorToRgbArray(color: string): number[] {
  const fallback = [0, 0, 0]
  const isRgb = color.substring(0, 3) === "rgb"
  const isHex = color.substring(0, 1) === "#"
  const isShortHex = isHex && color.length === 4
  let parts

  if (!isRgb && !isHex) {
    return fallback
  }

  // RGB values.
  if (isRgb) {
    parts = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  }

  // Hex values.
  if (isHex) {
    parts = isShortHex
      ? color.match(/^#([0-9a-z])([0-9a-z])([0-9a-z])$/i)
      : color.match(/^#([0-9a-z]{2})([0-9a-z]{2})([0-9a-z]{2})$/i)

    if (isShortHex) {
      parts = parts?.map((part) => `${part}${part}`)
    }
  }

  if (parts) {
    // Remove the match whole part from the regex match results.
    parts.shift()
  }

  return parts && parts.length === 3
    ? parts.map((part) => Number.parseInt(part, isHex ? 16 : 10))
    : fallback
}

/**
 * Returs true if the color value is dark.
 *
 * This utility can help determine whether bright or dark text should be used with the given color as a background.
 *
 * @see http://www.w3.org/TR/AERT#color-contrast
 */
export function isColorDark(color: string): boolean {
  const [r, g, b] = colorToRgbArray(color)
  const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000)
  return brightness < 125
}

/**
 * Return a color shade by darkening or lightening a color by the given amount.
 */
export function adjustColorLightness(color: string, amount: number): string {
  const adjust = (value: number) => {
    const nextValue = value + amount
    // Ensure that value stays in range of 0 - 255
    return Math.max(Math.min(nextValue, 255), 0)
  }
  const [r, g, b] = colorToRgbArray(color).map(adjust)
  return `rgb(${r}, ${g}, ${b})`
}
